import React, { useCallback, useEffect, useState } from "react";
import "./resellerStudio.css";
import { fetchStoryOpens, type StoryOpenRow } from "./catalogueResellerApi";

type Props = { catalogueId: string };

const ROLE_LABEL: Record<string, string> = { RESELLER: "Reseller", CLIENT: "Reseller's client" };

/**
 * Studio: which buyers opened which piece's making story. The story starts closed on shared pages,
 * so each open is a deliberate act of interest.
 */
const StoryOpensCard: React.FC<Props> = ({ catalogueId }) => {
  const [rows, setRows] = useState<StoryOpenRow[] | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await fetchStoryOpens(catalogueId);
      if (res.success && Array.isArray(res.data)) setRows(res.data);
      else {
        setRows([]);
        setError(res.message || "Could not load story opens.");
      }
    } catch {
      setRows([]);
      setError("Could not load story opens.");
    }
  }, [catalogueId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (rows === null) return <div className="hkp-studio-tree-muted">Loading story opens…</div>;

  const buyers = new Set(rows.map((r) => r.linkToken || r.viewerName || "")).size;

  return (
    <section className="hkp-studio-tree" aria-label="Making story opens">
      <header className="hkp-studio-tree-head">
        <h3>
          Making story opens
          {rows.length ? (
            <span className="hkp-studio-story-sub">
              {" "}
              · {buyers} buyer{buyers === 1 ? "" : "s"}, {rows.length} piece{rows.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </h3>
        <button type="button" className="hkp-studio-tree-refresh" onClick={() => void load()}>
          Refresh
        </button>
      </header>
      {error ? <p className="hkp-studio-tree-error">{error}</p> : null}
      {rows.length === 0 && !error ? (
        <p className="hkp-studio-tree-muted">
          No one has opened a piece's making story yet. Buyers see it closed and open it when a piece interests them.
        </p>
      ) : null}
      {rows.length ? (
        <table className="hkp-studio-tree-table hkp-studio-story-table">
          <thead>
            <tr>
              <th scope="col">Buyer</th>
              <th scope="col">Piece</th>
              <th scope="col" className="is-num">Opens</th>
              <th scope="col">Last opened</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.linkToken}-${r.productUniqueId}`}>
                <td>
                  <span className="hkp-studio-tree-client">{r.viewerName || "Buyer"}</span>
                  {r.linkRole && ROLE_LABEL[r.linkRole] ? (
                    <span className="hkp-studio-tree-phone">{ROLE_LABEL[r.linkRole]}</span>
                  ) : null}
                </td>
                <td>
                  <span className="hkp-studio-tree-client">{r.productName || r.productUniqueId}</span>
                  <span className="hkp-studio-tree-phone">{r.productUniqueId}</span>
                </td>
                <td className="is-num">{r.opens}</td>
                <td>{when(r.lastOpenedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
};

function when(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

export default StoryOpensCard;
